/**
 * LazySection
 *
 * Defers mounting of below-the-fold sections until they approach the viewport,
 * using an IntersectionObserver (via useLazyMount). While unmounted, a
 * height-preserving shimmer placeholder is rendered so the page layout stays
 * stable (no CLS).
 *
 * Usage:
 *   <LazySection minHeight="420px">
 *     <BestSellers />
 *   </LazySection>
 */

import { type ReactNode } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useLazyMount, type LazyMountOptions } from '@/lib/hooks/use-lazy-mount';

interface LazySectionProps extends LazyMountOptions {
  /** Minimum height of the placeholder, should approximate the rendered section height. */
  minHeight: string;
  children: ReactNode;
  /** Override the default shimmer skeleton with a custom element. */
  placeholder?: ReactNode;
  /** Accessible label for the loading placeholder region. */
  ariaLabel?: string;
}

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

const ShimmerPlaceholder = styled.div<{ $minHeight: string }>`
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.secondary} 0%,
    ${({ theme }) => theme.colors.background.primary}   50%,
    ${({ theme }) => theme.colors.background.secondary} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

export function LazySection({
  minHeight,
  children,
  placeholder,
  ariaLabel = 'Section loading',
  rootMargin,
  threshold,
}: Readonly<LazySectionProps>) {
  const { ref, shouldMount } = useLazyMount({ rootMargin, threshold });

  if (shouldMount) {
    return <>{children}</>;
  }

  return (
    <section ref={ref} aria-label={ariaLabel} aria-busy="true">
      {placeholder ?? <ShimmerPlaceholder $minHeight={minHeight} />}
    </section>
  );
}
